// src/firebase/config.js

import { initializeApp } from 'firebase/app';
import { 
    getFirestore, 
    Timestamp, 
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    connectFirestoreEmulator
} from 'firebase/firestore';
import { 
    getStorage, 
    connectStorageEmulator
} from 'firebase/storage';
import { 
    getAuth, 
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup, 
    onAuthStateChanged, 
    signOut, 
    connectAuthEmulator,
    signInWithRedirect,
    getRedirectResult,
    linkWithCredential
} from 'firebase/auth';
import { 
    getFunctions, 
    connectFunctionsEmulator
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: "AIzaSyCj5OX4S5bHh41slweZ-Zg90cGArTjUMMM",
  authDomain: "relief-roster-43593.firebaseapp.com",
  projectId: "relief-roster-43593",
  storageBucket: "relief-roster-43593.firebasestorage.app",
  messagingSenderId: "508648539179",
  appId: "1:508648539179:web:aaa664ccbe1a82d894166e"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);
const functions = getFunctions(app);

/**
 * Create Google Auth Provider with Calendar Scopes
 * This replaces the basic GoogleAuthProvider with calendar permissions
 */
function createGoogleProviderWithCalendar() {
  const provider = new GoogleAuthProvider();
  
  // Add calendar scopes for integration
  provider.addScope('https://www.googleapis.com/auth/calendar');
  provider.addScope('https://www.googleapis.com/auth/calendar.events');
  
  // Optional: Add these for better user info
  provider.addScope('https://www.googleapis.com/auth/userinfo.email');
  provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
  
  return provider;
}

/**
 * Create Microsoft Auth Provider with Office 365 Scopes
 * Supports both personal Microsoft accounts and organizational accounts
 * NOW WITH OUTLOOK CALENDAR INTEGRATION
 */
function createMicrosoftProvider() {
  const provider = new OAuthProvider('microsoft.com');
  
  // Configure tenant - use 'common' to support both personal and work accounts
  // You can change this to 'organizations' for work/school only, or specific tenant ID
  provider.setCustomParameters({
    tenant: 'common', // Allows both personal and organizational accounts
    prompt: 'select_account' // Always show account picker
  });
  
  // Add Office 365 scopes
  provider.addScope('openid');
  provider.addScope('profile'); 
  provider.addScope('email');
  provider.addScope('User.Read'); // Basic profile info
  
  // Add calendar scopes for Outlook Calendar integration
  provider.addScope('Calendars.ReadWrite');
  provider.addScope('Calendars.Read');
  
  return provider;
}

/**
 * Enhanced Google Sign-In with Calendar Permissions
 */
async function signInWithGoogleCalendar() {
  const provider = createGoogleProviderWithCalendar();
  
  try {
    console.log("Attempting Google sign-in with calendar permissions...");
    
    // Try popup method first
    const result = await signInWithPopup(auth, provider);
    console.log("Google sign-in successful:", result.user);
    
    // Check if we got calendar permissions
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (credential) {
      console.log("Google calendar permissions granted");
    }
    
    return result;
    
  } catch (error) {
    console.error("Google sign-in failed:", error);
    
    if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
      console.log("Popup failed, trying redirect method...");
      try {
        await signInWithRedirect(auth, provider);
      } catch (redirectError) {
        console.error("Redirect also failed:", redirectError);
        throw redirectError;
      }
    }
    throw error;
  }
}

/**
 * Microsoft/Office 365 Sign-In with Calendar Permissions
 * POPUP ONLY - No redirect fallback (avoids needing subdomain URIs in Azure)
 */
async function signInWithMicrosoft() {
  const provider = createMicrosoftProvider();
  
  try {
    console.log("🔵 Attempting Microsoft sign-in with popup...");
    const result = await signInWithPopup(auth, provider);
    
    console.log("🔵 Microsoft sign-in successful!");
    
    // Extract the OAuth credential to get the access token
    const credential = OAuthProvider.credentialFromResult(result);
    if (credential && credential.accessToken) {
      console.log("📅 Microsoft calendar access token obtained!");
      
      // Store the token in sessionStorage for persistence
      sessionStorage.setItem('outlookAccessToken', credential.accessToken);
      sessionStorage.setItem('outlookTokenExpiry', String(Date.now() + (55 * 60 * 1000)));
      
      // Initialize the Outlook Calendar Service
      outlookCalendarService.setCurrentUser(result.user, credential.accessToken);
      console.log("✅ Outlook Calendar Service initialized and ready");
    } else {
      console.warn("⚠️ No access token in Microsoft credential - calendar features won't work");
    }
    
    return result;
    
  } catch (error) {
    console.error("🔵 Microsoft sign-in error:", error.code, error.message);
    
    if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error("This email is already linked to another sign-in method. Please use the same sign-in method you used previously.");
    }
    
    if (error.code === 'auth/popup-blocked') {
      throw new Error("Sign-in popup was blocked. Please allow popups for this site in your browser settings, then try again.");
    }
    
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error("Sign-in was cancelled.");
    }
    
    if (error.code === 'auth/cancelled-popup-request') {
      throw new Error("Sign-in was interrupted. Please try again.");
    }
    
    // NO REDIRECT FALLBACK - just throw the error
    throw error;
  }
}

/**
 * Generic sign-in function that can handle both providers
 */
async function signInWithProvider(providerType = 'google') {
  switch (providerType.toLowerCase()) {
    case 'google':
      return await signInWithGoogleCalendar();
    case 'microsoft':
    case 'office365':
    case 'azure':
      return await signInWithMicrosoft();
    default:
      throw new Error(`Unsupported provider: ${providerType}`);
  }
}

/**
 * Modern Google Calendar Service using Google Identity Services
 */
class GoogleCalendarService {
  constructor() {
    this.gapi = null;
    this.google = null;
    this.isInitialized = false;
    this.currentUser = null;
    this.accessToken = null;
    this.tokenExpiry = null;
    this.tokenClient = null;
  }

  // Modern initialization using Google Identity Services
  async initializeGoogleAPIs() {
    if (this.isInitialized) {
      console.log("Calendar APIs already initialized");
      return;
    }

    console.log("Starting modern Google Calendar API initialization...");
    
    try {
      // Step 1: Load Google APIs and Google Identity Services
      await this.loadGoogleScripts();
      
      // Step 2: Initialize GAPI client (for API calls)
      await this.initializeGAPIClient();
      
      // Step 3: Initialize Google Identity Services (for auth)
      await this.initializeGoogleIdentityServices();
      
      this.isInitialized = true;
      console.log("Modern Google Calendar API initialized successfully");
      
    } catch (error) {
      console.error("Modern calendar API initialization failed:", error);
      this.isInitialized = false;
      console.warn("Continuing without calendar integration");
    }
  }

  // Load both GAPI and Google Identity Services scripts
  async loadGoogleScripts() {
    console.log("Loading Google API scripts...");
    
    // Load GAPI for API calls
    if (!window.gapi) {
      await this.loadScript('https://apis.google.com/js/api.js');
      this.gapi = window.gapi;
    } else {
      this.gapi = window.gapi;
    }
    
    // Load Google Identity Services for modern auth
    if (!window.google) {
      await this.loadScript('https://accounts.google.com/gsi/client');
      this.google = window.google;
    } else {
      this.google = window.google;
    }
    
    console.log("Google scripts loaded successfully");
  }

  // Helper to load scripts
  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  // Initialize GAPI client for API calls (no auth)
  async initializeGAPIClient() {
    console.log("Initializing GAPI client...");
    
    await new Promise((resolve, reject) => {
      this.gapi.load('client', {
        callback: async () => {
          try {
            await this.gapi.client.init({
              apiKey: firebaseConfig.apiKey,
              discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest']
              // Note: No clientId here - auth is handled separately by Google Identity Services
            });
            console.log("GAPI client initialized");
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        onerror: reject
      });
    });
  }

  // Initialize Google Identity Services for OAuth
  initializeGoogleIdentityServices() {
    console.log("Initializing Google Identity Services...");
    
    try {
      // Initialize the OAuth2 token client
      this.tokenClient = this.google.accounts.oauth2.initTokenClient({
        client_id: '508648539179-lsrqsg1g4f7230hbmjd8s6s8efaa3bj1.apps.googleusercontent.com',
        scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
        callback: (tokenResponse) => {
          console.log("Token received:", tokenResponse);
          this.accessToken = tokenResponse.access_token;
        },
        error_callback: (error) => {
          console.error("OAuth error:", error);
        }
      });
      
      console.log("Google Identity Services initialized");
    } catch (error) {
      console.error("Failed to initialize Google Identity Services:", error);
      throw error;
    }
  }

  // Set the current Firebase user
  setCurrentUser(firebaseUser) {
    console.log("Setting current user in calendar service:", firebaseUser?.email);
    this.currentUser = firebaseUser;
  }

  // Get Google OAuth access token using simpler method
async getAccessToken() {
  if (!this.currentUser) {
    throw new Error("No current user set in calendar service");
  }
  
  // Return cached token if still valid
  if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
    console.log("Using cached access token");
    return this.accessToken;
  }
  
  try {
    console.log("Getting fresh Google OAuth access token...");
    
    // Check if user signed in with Google
    const providersData = this.currentUser.providerData;
    const googleProvider = providersData.find(provider => provider.providerId === 'google.com');
    
    if (!googleProvider) {
      throw new Error("User did not sign in with Google - cannot access calendar");
    }
    
    // Request new token only if needed
    return new Promise((resolve, reject) => {
      if (!this.tokenClient) {
        reject(new Error("Token client not initialized"));
        return;
      }
      
      // Set up callback to handle the token response
      this.tokenClient.callback = (response) => {
        if (response.error) {
          console.error("Token request failed:", response);
          reject(new Error(`OAuth error: ${response.error}`));
        } else {
          console.log("Calendar access token received");
          
          // Cache the token and set expiry (tokens typically last 1 hour)
          this.accessToken = response.access_token;
          this.tokenExpiry = Date.now() + (55 * 60 * 1000); // 55 minutes from now
          
          resolve(response.access_token);
        }
      };
      
      // Request token - remove 'prompt: consent' to avoid re-prompting every time
      this.tokenClient.requestAccessToken({
        // Only prompt if we don't have a valid token
        prompt: this.accessToken ? '' : 'select_account'
      });
    });
    
  } catch (error) {
    console.error("Failed to get Google access token:", error);
    throw error;
  }
}

  // Check if user has calendar permissions
  async hasCalendarPermissions() {
    if (!this.currentUser) {
      console.log("No current user - no calendar permissions");
      return false;
    }
    
    try {
      // Try to get an access token (this will prompt if needed)
      const token = await this.getAccessToken();
      
      // Test the token by making a simple API call
      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/calendars/primary',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const hasPermissions = response.ok;
      console.log("Calendar permissions check result:", hasPermissions);
      return hasPermissions;
    } catch (error) {
      console.log('No calendar permissions (error):', error);
      return false;
    }
  }

// Method to look up period times from Firestore
async getPeriodTimes(tenantId, period) {
  try {
    console.log(`Looking up times for period ${period} in tenant ${tenantId}`);
    
    // Get the schedule_types document for this tenant
    const scheduleDocRef = doc(db, 'tenantConfigurations', `${tenantId}_schedule_types`);
    const scheduleSnap = await getDoc(scheduleDocRef);
    
    if (scheduleSnap.exists()) {
      const scheduleData = scheduleSnap.data();
      console.log(`Schedule data found:`, scheduleData);
      
      // Look through the periods array to find the matching period
      const periods = scheduleData.periods || [];
      const foundPeriod = periods.find(p => p.id === period || p.name === period);
      
      if (foundPeriod) {
        console.log(`Found period ${period}:`, foundPeriod);
        return {
          startTime: foundPeriod.start_time || '08:00',
          endTime: foundPeriod.end_time || '09:00'
        };
      } else {
        console.warn(`Period ${period} not found in periods array, using defaults`);
        console.log(`Available periods:`, periods.map(p => `${p.id} (${p.name})`));
        return {
          startTime: '08:00',
          endTime: '15:30'
        };
      }
    } else {
      console.warn(`No schedule document found for tenant ${tenantId}, using defaults`);
      return {
        startTime: '08:00',
        endTime: '15:30'
      };
    }
  } catch (error) {
    console.error(`Error looking up period ${period}:`, error);
    // Return defaults if lookup fails
    return {
      startTime: '08:00',
      endTime: '15:30'
    };
  }
}

  // Create substitute job calendar event - UPDATED WITH FIXED PERIOD TIMES
  async createSubstituteJobEvent(requestData, substituteInfo) {
  console.log("Creating substitute job event with period-specific times...");

  if (!this.isInitialized) {
    throw new Error("Calendar service not initialized - call initializeGoogleAPIs() first");
  }
  
  if (!this.currentUser) {
    throw new Error("No user set in calendar service - call setCurrentUser() first");
  }

  // Check if explicit period times were provided (from AvailableSignups.js)
  let periodTimes = { startTime: '08:00', endTime: '15:30' }; // defaults
  
  if (requestData.explicitPeriodTimes) {
    console.log("Using explicit period times from AvailableSignups:", requestData.explicitPeriodTimes);
    periodTimes = {
      startTime: requestData.explicitPeriodTimes.startTime,
      endTime: requestData.explicitPeriodTimes.endTime
    };
    console.log("EXPLICIT TIMES APPLIED:", periodTimes);
  } else if (requestData.periods && requestData.periods.length > 0 && requestData.tenantId) {
    // Fallback to lookup if no explicit times provided
    console.log("No explicit times provided, attempting period lookup...");
    try {
      periodTimes = await this.getPeriodTimes(requestData.tenantId, requestData.periods[0]);
    } catch (error) {
      console.warn("Failed to lookup period times, using defaults:", error);
    }
  } else {
    console.log("No period data available, using default times");
  }

  // Log final times being used
  console.log("Final event times being used:", {
    period: requestData.periods?.[0],
    startTime: periodTimes.startTime,
    endTime: periodTimes.endTime,
    date: requestData.date
  });

  const event = {
    summary: `SUB JOB - ${requestData.teacherName || 'Teacher'}`,
    description: this.formatSubstituteJobDescription(requestData, substituteInfo),
    start: {
      dateTime: this.formatDateTime(requestData.date, periodTimes.startTime),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    end: {
      dateTime: this.formatDateTime(requestData.date, periodTimes.endTime),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    location: `${requestData.schoolName || ''} - Room ${requestData.roomNumber || 'TBD'}`,
    colorId: '10', // Green color for sub job
    reminders: {
      useDefault: false,
      overrides: [
        { method: 'popup', minutes: 120 },
        { method: 'popup', minutes: 30 }
      ]
    }
  };

  console.log("Event data prepared with period times:", {
    period: requestData.periods?.[0],
    startTime: periodTimes.startTime,
    endTime: periodTimes.endTime,
    formattedStart: event.start.dateTime,
    formattedEnd: event.end.dateTime,
    event
  });

  try {
    // Get access token
    console.log("Getting access token...");
    const token = await this.getAccessToken();
    
    // Set token for GAPI
    console.log("Setting token in GAPI client...");
    this.gapi.client.setToken({ access_token: token });
    
    // Create the event
    console.log("Creating calendar event...");
    const response = await this.gapi.client.calendar.events.insert({
      calendarId: 'primary',
      resource: event
    });
    
    console.log('Substitute job event created successfully with period times:', response.result);
    return response.result;
  } catch (error) {
    console.error('Failed to create substitute job event:', error);
    throw error;
  }
}

  // Create teacher absence event
  async createTeacherAbsenceEvent(requestData) {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error("Calendar service not initialized or no user");
    }

    console.log("Creating teacher absence event...");

    const event = {
      summary: `ABSENT - Sub Requested`,
      description: this.formatTeacherAbsenceDescription(requestData),
      start: {
        dateTime: this.formatDateTime(requestData.date, requestData.startTime || '08:00'),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: this.formatDateTime(requestData.date, requestData.endTime || '15:30'),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      colorId: '11', // Red color for absence
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'popup', minutes: 60 },
          { method: 'popup', minutes: 15 }
        ]
      }
    };

    try {
      const token = await this.getAccessToken();
      this.gapi.client.setToken({ access_token: token });
      
      const response = await this.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event
      });
      
      console.log('Teacher absence event created:', response.result);
      return response.result;
    } catch (error) {
      console.error('Failed to create teacher absence event:', error);
      throw error;
    }
  }

  // Update calendar event
  async updateCalendarEvent(eventId, updatedData) {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error("Calendar service not initialized or no user");
    }

    try {
      const token = await this.getAccessToken();
      this.gapi.client.setToken({ access_token: token });
      
      const response = await this.gapi.client.calendar.events.patch({
        calendarId: 'primary',
        eventId: eventId,
        resource: updatedData
      });
      
      console.log('Calendar event updated:', response.result);
      return response.result;
    } catch (error) {
      console.error('Failed to update calendar event:', error);
      throw error;
    }
  }

  // Delete calendar event
  async deleteCalendarEvent(eventId) {
    if (!this.isInitialized || !this.currentUser) {
      throw new Error("Calendar service not initialized or no user");
    }

    try {
      const token = await this.getAccessToken();
      this.gapi.client.setToken({ access_token: token });
      
      await this.gapi.client.calendar.events.delete({
        calendarId: 'primary',
        eventId: eventId
      });
      
      console.log('Calendar event deleted:', eventId);
      return true;
    } catch (error) {
      console.error('Failed to delete calendar event:', error);
      throw error;
    }
  }

  // Format description for teacher absence
  formatTeacherAbsenceDescription(requestData) {
    return `
SUBSTITUTE REQUEST

Subject: ${requestData.subject || 'N/A'}
School: ${requestData.schoolName || 'N/A'}
Room: ${requestData.roomNumber || 'N/A'}
Periods: ${requestData.periods ? requestData.periods.join(', ') : 'All Day'}

Notes for Substitute:
${requestData.notes || 'No additional notes'}

Lesson Plans:
${requestData.lessonPlans || 'See shared materials'}

Emergency Contact: ${requestData.emergencyContact || 'See office'}

Status: ${requestData.status || 'Pending'}
Request ID: ${requestData.id || 'N/A'}
    `.trim();
  }

  // Format description for substitute job
  formatSubstituteJobDescription(requestData, substituteInfo) {
    return `
SUBSTITUTE ASSIGNMENT

Teacher: ${requestData.teacherName || 'N/A'}
Subject: ${requestData.subject || 'N/A'}
School: ${requestData.schoolName || 'N/A'}
Room: ${requestData.roomNumber || 'N/A'}
Periods: ${requestData.periods ? requestData.periods.join(', ') : 'All Day'}

Instructions:
${requestData.notes || 'No additional notes'}

Lesson Plans:
${requestData.lessonPlans || 'Check with office'}

Emergency Contact: ${requestData.emergencyContact || 'See office'}
Teacher Contact: ${requestData.teacherEmail || 'N/A'}

Accepted by: ${substituteInfo.name || 'N/A'}
Sub Email: ${substituteInfo.email || 'N/A'}
Sub Contact: ${substituteInfo.phone || 'N/A'}

Request ID: ${requestData.id || 'N/A'}
    `.trim();
  }

  // Format date/time for Google Calendar - FIXED VERSION
  // Format date/time for Google Calendar - FIXED VERSION WITH TIMEZONE
formatDateTime(date, time) {
  let dateStr;
  if (typeof date === 'string') {
    dateStr = date;
  } else if (date instanceof Date) {
    dateStr = date.toISOString().split('T')[0];
  } else if (date && date.toDate) {
    dateStr = date.toDate().toISOString().split('T')[0];
  } else {
    dateStr = new Date().toISOString().split('T')[0];
  }
  
  // Handle time formatting more carefully
  let timeStr = time;
  if (!time.includes(':')) {
    timeStr = `${time}:00`;
  }
  // Ensure we have seconds if we only have hours:minutes
  const timeParts = timeStr.split(':');
  if (timeParts.length === 2) {
    timeStr = `${timeStr}:00`;
  }
  
  // Create a proper Date object with timezone
  const dateTimeStr = `${dateStr}T${timeStr}`;
  const dateTime = new Date(dateTimeStr);
  
  // Return ISO string which Google Calendar handles better
  const result = dateTime.toISOString();
  console.log(`Formatted DateTime: ${time} → ${dateTimeStr} → ${result}`);
  return result;
}
}

// Create a singleton instance
const calendarService = new GoogleCalendarService();

/**
 * Outlook Calendar Service using Microsoft Graph API
 * Handles calendar operations for Microsoft/Office 365 users
 */
class OutlookCalendarService {
  constructor() {
    this.currentUser = null;
    this.accessToken = null;
    this.tokenExpiry = null;
    this.graphApiBaseUrl = 'https://graph.microsoft.com/v1.0';
    
    // Try to restore token from sessionStorage on initialization
    this.restoreTokenFromStorage();
  }

  // NEW: Restore token from sessionStorage (survives page refresh)
  restoreTokenFromStorage() {
    try {
      const storedToken = sessionStorage.getItem('outlookAccessToken');
      const storedExpiry = sessionStorage.getItem('outlookTokenExpiry');
      
      if (storedToken && storedExpiry) {
        const expiry = parseInt(storedExpiry, 10);
        if (Date.now() < expiry) {
          console.log('📅 Restored Outlook access token from session storage');
          this.accessToken = storedToken;
          this.tokenExpiry = expiry;
        } else {
          console.log('📅 Stored Outlook token expired, clearing...');
          sessionStorage.removeItem('outlookAccessToken');
          sessionStorage.removeItem('outlookTokenExpiry');
        }
      }
    } catch (error) {
      console.warn('Could not restore Outlook token from storage:', error);
    }
  }

  // Set the current Firebase user and their access token
  setCurrentUser(user, accessToken) {
    console.log("Setting current user in Outlook calendar service:", user?.email);
    this.currentUser = user;
    
    if (accessToken) {
      this.accessToken = accessToken;
      this.tokenExpiry = Date.now() + (55 * 60 * 1000); // 55 minutes
      
      // Store in sessionStorage for persistence
      try {
        sessionStorage.setItem('outlookAccessToken', accessToken);
        sessionStorage.setItem('outlookTokenExpiry', String(this.tokenExpiry));
        console.log('📅 Outlook access token stored in session storage');
      } catch (error) {
        console.warn('Could not store Outlook token in session storage:', error);
      }
    } else if (!this.accessToken) {
      // Try to restore from storage if no token provided
      this.restoreTokenFromStorage();
    }
  }

  // Check if we have a valid access token
  hasValidToken() {
    if (!this.accessToken) {
      console.log("No Outlook access token available");
      return false;
    }
    
    if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
      console.log("Outlook access token expired");
      return false;
    }
    
    return true;
  }

  // NEW: hasCalendarPermissions() - REQUIRED BY calendarHelper.js
  // This method must exist and match the signature of GoogleCalendarService
  async hasCalendarPermissions() {
    if (!this.hasValidToken()) {
      console.log('📅 Outlook: No valid token - no calendar permissions');
      return false;
    }
    
    try {
      // Test the token by making a simple API call to check calendar access
      const response = await fetch(
        `${this.graphApiBaseUrl}/me/calendar`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const hasPermissions = response.ok;
      console.log('📅 Outlook calendar permissions check:', hasPermissions);
      return hasPermissions;
    } catch (error) {
      console.error('📅 Outlook calendar permissions check failed:', error);
      return false;
    }
  }

  // Get calendar events from Outlook
  async getEvents(startDate, endDate) {
    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    try {
      const startDateTime = startDate.toISOString();
      const endDateTime = endDate.toISOString();
      
      const response = await fetch(
        `${this.graphApiBaseUrl}/me/calendar/calendarView?startDateTime=${startDateTime}&endDateTime=${endDateTime}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.value || [];
    } catch (error) {
      console.error("Error fetching Outlook calendar events:", error);
      throw error;
    }
  }

  // Create a calendar event in Outlook
  async createEvent(eventData) {
    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    try {
      const event = {
        subject: eventData.summary || eventData.subject,
        body: {
          contentType: "text",
          content: eventData.description || ''
        },
        start: {
          dateTime: eventData.startTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: eventData.endTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        location: {
          displayName: eventData.location || ''
        },
        // Add reminder
        reminderMinutesBeforeStart: eventData.reminderMinutes || 30,
        isReminderOn: true
      };

      console.log('📅 Creating Outlook event:', event);

      const response = await fetch(
        `${this.graphApiBaseUrl}/me/events`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(event)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Graph API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      const createdEvent = await response.json();
      console.log("✅ Outlook calendar event created:", createdEvent.id);
      return createdEvent;
    } catch (error) {
      console.error("❌ Error creating Outlook calendar event:", error);
      throw error;
    }
  }

  // RENAMED: updateCalendarEvent (was updateEvent) - matches calendarHelper.js expectations
  async updateCalendarEvent(eventId, eventData) {
    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    try {
      const event = {
        subject: eventData.summary || eventData.subject,
        body: {
          contentType: "text",
          content: eventData.description || ''
        },
        start: {
          dateTime: eventData.startTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: eventData.endTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        location: {
          displayName: eventData.location || ''
        }
      };

      const response = await fetch(
        `${this.graphApiBaseUrl}/me/events/${eventId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(event)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Graph API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      const updatedEvent = await response.json();
      console.log("✅ Outlook calendar event updated:", updatedEvent.id);
      return updatedEvent;
    } catch (error) {
      console.error("❌ Error updating Outlook calendar event:", error);
      throw error;
    }
  }

  // RENAMED: deleteCalendarEvent (was deleteEvent) - matches calendarHelper.js expectations
  async deleteCalendarEvent(eventId) {
    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    try {
      const response = await fetch(
        `${this.graphApiBaseUrl}/me/events/${eventId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok && response.status !== 404) {
        const errorData = await response.json();
        throw new Error(`Graph API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      console.log("✅ Outlook calendar event deleted:", eventId);
      return true;
    } catch (error) {
      console.error("❌ Error deleting Outlook calendar event:", error);
      throw error;
    }
  }

  // Method to look up period times from Firestore
  async getPeriodTimes(tenantId, period) {
    try {
      console.log(`Looking up times for period ${period} in tenant ${tenantId}`);
      
      // Import db from the same file or pass it in
      const { db, doc, getDoc } = await import('firebase/firestore');
      
      const scheduleDocRef = doc(db, 'tenantConfigurations', `${tenantId}_schedule_types`);
      const scheduleSnap = await getDoc(scheduleDocRef);
      
      if (scheduleSnap.exists()) {
        const scheduleData = scheduleSnap.data();
        const periods = scheduleData.periods || [];
        const foundPeriod = periods.find(p => p.id === period || p.name === period);
        
        if (foundPeriod) {
          console.log(`Found period ${period}:`, foundPeriod);
          return {
            startTime: foundPeriod.start_time || '08:00',
            endTime: foundPeriod.end_time || '09:00'
          };
        }
      }
      
      console.warn(`Period ${period} not found, using defaults`);
      return {
        startTime: '08:00',
        endTime: '15:30'
      };
    } catch (error) {
      console.error(`Error looking up period ${period}:`, error);
      return {
        startTime: '08:00',
        endTime: '15:30'
      };
    }
  }

  // Create substitute job calendar event
  async createSubstituteJobEvent(requestData, substituteInfo) {
    console.log("📅 Creating substitute job event in Outlook...");

    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    // Get period times
    let periodTimes = { startTime: '08:00', endTime: '15:30' };
    
    if (requestData.explicitPeriodTimes) {
      console.log("Using explicit period times:", requestData.explicitPeriodTimes);
      periodTimes = {
        startTime: requestData.explicitPeriodTimes.startTime,
        endTime: requestData.explicitPeriodTimes.endTime
      };
    } else if (requestData.startTime && requestData.endTime) {
      periodTimes = {
        startTime: requestData.startTime,
        endTime: requestData.endTime
      };
    }

    console.log("📅 Final event times:", {
      period: requestData.periods?.[0],
      startTime: periodTimes.startTime,
      endTime: periodTimes.endTime,
      date: requestData.date
    });

    const eventData = {
      summary: `SUB JOB - ${requestData.teacherName || 'Teacher'}`,
      description: this.formatSubstituteJobDescription(requestData, substituteInfo),
      startTime: this.formatDateTime(requestData.date, periodTimes.startTime),
      endTime: this.formatDateTime(requestData.date, periodTimes.endTime),
      location: `${requestData.schoolName || ''} - Room ${requestData.roomNumber || 'TBD'}`,
      reminderMinutes: requestData.reminderMinutes || 30
    };

    return await this.createEvent(eventData);
  }

  // Create teacher absence event
  async createTeacherAbsenceEvent(requestData) {
    console.log("📅 Creating teacher absence event in Outlook...");

    if (!this.hasValidToken()) {
      throw new Error("No valid access token. Please sign in again.");
    }

    const eventData = {
      summary: `ABSENT - Sub Requested`,
      description: this.formatTeacherAbsenceDescription(requestData),
      startTime: this.formatDateTime(requestData.date, requestData.startTime || '08:00'),
      endTime: this.formatDateTime(requestData.date, requestData.endTime || '15:30'),
      location: '',
      reminderMinutes: requestData.reminderMinutes || 60
    };

    return await this.createEvent(eventData);
  }

  // Format description for teacher absence
  formatTeacherAbsenceDescription(requestData) {
    return `
SUBSTITUTE REQUEST

Subject: ${requestData.subject || 'N/A'}
School: ${requestData.schoolName || 'N/A'}
Room: ${requestData.roomNumber || 'N/A'}
Periods: ${requestData.periods ? requestData.periods.join(', ') : 'All Day'}

Notes for Substitute:
${requestData.notes || 'No additional notes'}

Lesson Plans:
${requestData.lessonPlans || 'See shared materials'}

Emergency Contact: ${requestData.emergencyContact || 'See office'}

Status: ${requestData.status || 'Pending'}
Request ID: ${requestData.id || 'N/A'}
    `.trim();
  }

  // Format description for substitute job
  formatSubstituteJobDescription(requestData, substituteInfo) {
    return `
SUBSTITUTE ASSIGNMENT

Teacher: ${requestData.teacherName || 'N/A'}
Subject: ${requestData.subject || 'N/A'}
School: ${requestData.schoolName || 'N/A'}
Room: ${requestData.roomNumber || 'N/A'}
Periods: ${requestData.periods ? requestData.periods.join(', ') : 'All Day'}

Instructions:
${requestData.notes || 'No additional notes'}

Lesson Plans:
${requestData.lessonPlans || 'Check with office'}

Emergency Contact: ${requestData.emergencyContact || 'See office'}
Teacher Contact: ${requestData.teacherEmail || 'N/A'}

Accepted by: ${substituteInfo?.name || 'N/A'}
Sub Email: ${substituteInfo?.email || 'N/A'}
Sub Contact: ${substituteInfo?.phone || 'N/A'}

Request ID: ${requestData.id || 'N/A'}
    `.trim();
  }

  // Format date/time for Outlook Calendar
  formatDateTime(date, time) {
    let dateStr;
    if (typeof date === 'string') {
      dateStr = date.split('T')[0]; // Handle if already ISO string
    } else if (date instanceof Date) {
      dateStr = date.toISOString().split('T')[0];
    } else if (date && date.toDate) {
      dateStr = date.toDate().toISOString().split('T')[0];
    } else {
      dateStr = new Date().toISOString().split('T')[0];
    }
    
    // Handle time formatting
    let timeStr = time || '08:00';
    if (!timeStr.includes(':')) {
      timeStr = `${timeStr}:00`;
    }
    const timeParts = timeStr.split(':');
    if (timeParts.length === 2) {
      timeStr = `${timeStr}:00`;
    }
    
    // Create ISO string
    const dateTimeStr = `${dateStr}T${timeStr}`;
    const dateTime = new Date(dateTimeStr);
    const result = dateTime.toISOString();
    
    console.log(`📅 Formatted DateTime for Outlook: ${time} → ${result}`);
    return result;
  }
}

// Create a singleton instance for Outlook Calendar
const outlookCalendarService = new OutlookCalendarService();

/**
 * Fetches the user profile from faculty_staff collection by email
 * Since your system uses composite IDs like {tenantId}_{sisId}, we need to query by email
 * Now supports both Google and Microsoft users
 */
async function getUserProfile(uid, email) {
  if (!uid || !email) return null;
  
  try {
    // First try the old users collection (for backward compatibility)
    const userDocRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      console.log('Found user in users collection');
      return userSnap.data();
    }

    // If not found, search faculty_staff by email
    console.log('Searching faculty_staff by email:', email);
    const facultyQuery = query(
      collection(db, 'faculty_staff'),
      where('email', '==', email.toLowerCase())
    );
    
    const querySnapshot = await getDocs(facultyQuery);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      const docId = querySnapshot.docs[0].id;
      
      console.log('Found user in faculty_staff:', docId);
      
      // Return user data in the format your app expects
      return {
        ...userData,
        docId: docId,
        uid: uid,
        displayName: userData.name || `${userData.firstName} ${userData.lastName}`,
        // Map role-based permissions
        isSystemAdmin: userData.role === 'System Administrator',
        isEducationalAdmin: userData.role === 'Educational Administrator',
        isHR: userData.role === 'HR',
        isSupervisor: userData.isSupervisor || false
      };
    } else {
      console.warn(`No user document found for email: ${email}`);
      return null;
    }
  } catch (error) {
    console.error("Error in getUserProfile:", error);
    return null;
  }
}

/**
 * Enhanced auth state change handler that fetches user profile
 * Now also initializes calendar service properly and supports both Google and Microsoft users
 */
function setupAuthStateListener(callback) {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      console.log('Firebase user detected:', firebaseUser.uid, firebaseUser.email);
      
      // Check what provider the user signed in with
      const providers = firebaseUser.providerData.map(p => p.providerId);
      console.log('User signed in with providers:', providers);
      
      const hasGoogleProvider = providers.includes('google.com');
      const hasMicrosoftProvider = providers.includes('microsoft.com');
      
      // Initialize Google Calendar service for Google users
      if (hasGoogleProvider) {
        calendarService.setCurrentUser(firebaseUser);
        try {
          console.log('Initializing Google Calendar service...');
          await calendarService.initializeGoogleAPIs();
          console.log('Google Calendar service ready');
        } catch (calendarError) {
          console.warn('Google Calendar service initialization failed:', calendarError);
        }
      }
      
      // Initialize Outlook Calendar service for Microsoft users
      if (hasMicrosoftProvider) {
        console.log('🔵 Microsoft user detected - initializing Outlook Calendar...');
        // The token should be restored from sessionStorage by the constructor
        outlookCalendarService.setCurrentUser(firebaseUser, null); // null = try to restore from storage
        
        if (outlookCalendarService.hasValidToken()) {
          console.log('✅ Outlook Calendar service has valid token');
        } else {
          console.log('⚠️ Outlook Calendar token expired/missing - user needs to sign in again for calendar features');
        }
      }
      
      // Get the user profile from your database
      const userProfile = await getUserProfile(firebaseUser.uid, firebaseUser.email);
      
      if (userProfile) {
        console.log('User profile found:', userProfile);
        
        try {
          const idTokenResult = await firebaseUser.getIdTokenResult(true);
          console.log('Custom claims:', idTokenResult.claims);
          
          // Check calendar permissions based on provider
          let hasCalendarPerms = false;
          let calendarProvider = 'none';
          
          if (hasGoogleProvider) {
            try {
              hasCalendarPerms = await calendarService.hasCalendarPermissions();
              if (hasCalendarPerms) calendarProvider = 'google';
              console.log('Google Calendar permissions:', hasCalendarPerms);
            } catch (calendarPermError) {
              console.warn('Google Calendar permissions check failed:', calendarPermError);
            }
          } else if (hasMicrosoftProvider) {
            // For Microsoft, check if we have a valid token
            hasCalendarPerms = outlookCalendarService.hasValidToken();
            if (hasCalendarPerms) calendarProvider = 'microsoft';
            console.log('Outlook Calendar has valid token:', hasCalendarPerms);
          }
          
          // Merge Firebase user with profile data
          const completeUser = {
            ...userProfile,
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            // Add provider information
            providers: providers,
            signInProvider: providers[0],
            // Add custom claims to user object
            customClaims: idTokenResult.claims,
            // Add calendar status - NOW WORKS FOR BOTH PROVIDERS
            hasCalendarPermissions: hasCalendarPerms,
            calendarProvider: calendarProvider  // 'google', 'microsoft', or 'none'
          };
          
          callback(completeUser);
        } catch (tokenError) {
          console.error('Error getting ID token:', tokenError);
          callback(userProfile);
        }
      } else {
        console.warn('Firebase user found but no profile data');
        callback(null);
      }
    } else {
      console.log('No Firebase user');
      callback(null);
    }
  });
}

// DEBUG: Add authentication and custom claims debugging
onAuthStateChanged(auth, async (user) => {
  if (user) {
    console.log('=== USER DEBUG INFO ===');
    console.log('User is signed in:', user.email);
    console.log('User UID:', user.uid);
    
    try {
      const token = await user.getIdTokenResult();
      console.log('Custom Claims:', token.claims);
      console.log('Token Role:', token.claims.role);
      console.log('Token Tenant ID:', token.claims.tenantId);
      console.log('Firestore Doc ID:', token.claims.firestoreDocId);
      console.log('Has Elevated Privileges:', token.claims.hasElevatedPrivileges);
      console.log('Is Supervisor:', token.claims.isSupervisor);
    } catch (error) {
      console.log('Error getting token:', error);
    }
    
    console.log('=== END DEBUG ===');
  } else {
    console.log('User is signed out');
  }
});

// Export everything needed by the app
export { 
    app,
    db, 
    storage, 
    auth, 
    functions,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup, 
    onAuthStateChanged, 
    signOut, 
    Timestamp,
    signInWithRedirect,
    getRedirectResult,
    getUserProfile,
    setupAuthStateListener,
    // GOOGLE AUTH EXPORTS
    createGoogleProviderWithCalendar,
    signInWithGoogleCalendar,
    // MICROSOFT AUTH EXPORTS
    createMicrosoftProvider,
    signInWithMicrosoft,
    // GENERIC AUTH EXPORT
    signInWithProvider,
    // CALENDAR EXPORTS
    calendarService,
    GoogleCalendarService,
    outlookCalendarService,
    OutlookCalendarService
};